import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, HStack } from '@chakra-ui/react';

import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';

import { ReceiptInformationFormSchema } from '../../schema';
import { VatRateField } from '../VatRateField';
import locales from './locales';
import { computeRateFromVatAmount, computeVatAmountFromRate } from './utils';

interface Props {
  index: number;
  showAddButton: boolean;
  isTrashButtonDisabled: boolean;
  onAddItem: (index: number) => void;
  onRemoveItem: (index: number) => void;
}

export const VatItem: FC<Props> = ({
  index,
  isTrashButtonDisabled,
  onAddItem,
  onRemoveItem,
  showAddButton,
}) => {
  const { control, formState, register, setValue, watch } =
    useFormContext<ReceiptInformationFormSchema>();

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (type === 'change') {
        if (
          name === `amount.vatItems.${index}.rate` ||
          name === 'amount.total'
        ) {
          if (
            values.amount?.total !== undefined &&
            values.amount?.vatItems?.[index]?.rate !== undefined &&
            !Number.isNaN(values.amount.total) &&
            !Number.isNaN(values.amount.vatItems?.[index]?.rate)
          ) {
            setValue(
              `amount.vatItems.${index}.amount`,
              computeVatAmountFromRate(
                values.amount?.vatItems?.[index]?.rate ?? 0,
                values.amount?.total ?? 0,
              ),
              {
                shouldValidate: true,
              },
            );
          }
        }

        if (name === `amount.vatItems.${index}.amount`) {
          if (
            values.amount?.total !== undefined &&
            values.amount?.vatItems?.[index]?.amount !== undefined &&
            !Number.isNaN(values.amount?.total) &&
            !Number.isNaN(values.amount?.vatItems?.[index]?.amount)
          ) {
            setValue(
              `amount.vatItems.${index}.rate`,
              computeRateFromVatAmount(
                values.amount?.vatItems?.[index]?.amount ?? 0,
                values.amount.total,
              ),
              {
                shouldValidate: true,
              },
            );
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [setValue, index, watch]);
  const { errors } = formState;

  return (
    <HStack spacing="space-16">
      <Form.Field
        error={errors.amount?.vatItems?.[index]?.rate?.message}
        flex={1}
        label={locales.labels.rate}
      >
        <VatRateField
          control={control}
          name={`amount.vatItems.${index}.rate`}
          placeholder={locales.placeholders.rate}
        />
      </Form.Field>
      <Form.Field
        error={errors.amount?.vatItems?.[index]?.amount?.message}
        flex={1}
        label={locales.labels.amount}
      >
        <Form.Input
          inputMode="numeric"
          placeholder={locales.placeholders.amount}
          rightIcon="euro"
          step={0.01}
          type="number"
          {...register(`amount.vatItems.${index}.amount`, {
            valueAsNumber: true,
          })}
        />
      </Form.Field>
      <HStack alignSelf="flex-end" paddingBottom="space-4" spacing="inherit">
        <IconButton
          aria-label="remove item"
          icon="trash"
          isDisabled={isTrashButtonDisabled}
          onClick={() => {
            if (isTrashButtonDisabled) {
              return;
            }
            onRemoveItem(index);
          }}
          variant="secondary"
        />
        {showAddButton ? (
          <IconButton
            aria-label="add item"
            icon="plus"
            onClick={() => onAddItem(index)}
            variant="secondary"
          />
        ) : (
          <Box boxSize={40} visibility="hidden" />
        )}
      </HStack>
    </HStack>
  );
};
